import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";

// Helpers
import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import { accountFormat } from "@/Common/helpers/number";
import { errorLogger, getError } from "@/Common/helpers/logger";
import { computeProps, isFooterCell } from "@/Common/helpers/Tables";
import { measureTextWidth } from "@/Common/helpers/measure";
import { and, or } from "@/Common/helpers/sonar";
import { getAlternativeInput, getInputData } from "@/Washouts/helpers/Compensation";

// Custom components
import CompensationTooltip from "./CompensationTooltip";
import SpecialRowRender from "./SpecialRowRender";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { CompensationLoadingOverlay } from "./CompensationLoadingOverlay";
import { CompensationRowAdder } from "./CompensationRowAdder";
import { CommissionEditCell } from "./CommissionEditCell";
import { BasicTextEditCell } from "./BasicTextEditCell";

// Custom table validations
import { emptyValidation } from "@/Layouts/components/Tables/DataSmartTable/validation/TableValidations";

// Materials
import {
	useGridApiRef,
	GridColDef,
	GridCellParams,
	GridColumnHeaderParams,
	GridValidRowModel,
	GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { Stack, Tooltip, Typography } from "@mui/material";

// Types
import { IUser } from "@/Common/interfaces/user";
import { Nullable, StringMap } from "@/Common/interfaces/types";
import { CellDefinitions, CompensationDataRecord, CompensationTableProps, getCellTypeParams } from "./types";
import { ClassNameHints } from "@/Common/types/tables";
import { IColumnsAddonsRow } from "@/Washouts/types/Api/commons";

// Constants
import { ID_GRID_COLDEF, COLUMNS_ADDONS_DEF } from "@/Washouts/constants";

// Icons
import InfoIcon from "@Washouts/assets/icons/info-grey.svg";
import { NumericTierEditCell } from "./NumericTierEditCell";

// Local helpers
const subTotalLabel = "COMMISSION SUBTOTAL";
const isCellEditable = (params: GridCellParams) => {
	const isSubTotalRow = params?.row?.item?.toUpperCase() === subTotalLabel;
	if (isSubTotalRow) {
		return false;
	}
	const isCommissionField = params.colDef.field === "commission";
	const isAddedCompensationRow = !isNaN(Number(params?.row?.rowIndex));
	if (isAddedCompensationRow && !isCommissionField) {
		return true;
	}
	return isCommissionField;
};
const isSubTotal = (item: Nullable<string>) => item?.toUpperCase() === subTotalLabel;
const isNegativeValue = (value: number) => Math.sign(value) === -1;
const isDifferentCommission = (commission: unknown, originalCommission: unknown) =>
	Number(commission).toFixed(2) !== Number(originalCommission).toFixed(2);

const percentValues = ["PERCENT", "PERCENTAGE", "PERCENT PER UNIT"];
const dollarValues = ["DOLLAR"];
const dollarPerUnitValues = ["DOLLAR PER UNIT"];
const unitValues = ["UNIT"];
const hoursValues = ["HOUR", "HOURS", "HRS", "HR", "DURATION"];
const isInSpecificType = (value: Nullable<string>, types: string[] = []) => types.includes(value?.toUpperCase() ?? "");
const isCellValuePercentage = (value: unknown, type = "") => isNumeric(value) && isInSpecificType(type, percentValues);
const isCellValueDollar = (value: unknown, type = "") => isNumeric(value) && isInSpecificType(type, dollarValues);
const isCellDollarPerUnit = (value: unknown, type = "") => isNumeric(value) && isInSpecificType(type, dollarPerUnitValues);
const isCellUnit = (value: unknown, type = "") => isNumeric(value) && isInSpecificType(type, unitValues);
const isCellHours = (value: unknown, type = "") => isNumeric(value) && isInSpecificType(type, hoursValues);
const isEmptyValue = (value: Nullable<string>) => value === "" || value === null || value === undefined;

const FONT_SIZE_IN_PX = 14;
const FONT_LINE_HEIGHT_IN_PX = 22;
const DEFAULT_ROW_HEIGHT = 28;

const getCellClasses = (defaults: string, givenHints: Partial<ClassNameHints>, cellParams?: GridCellParams) => {
	const hints = { ...computeProps("", cellParams, []), ...givenHints };
	const hasNoEarnings = (hints.isSubTotalRow && !cellParams?.row?.commission);
	return clsx({
		[defaults]: true,
		"!bg-[color:--primary-color] !text-white": !hints.isNegative && hints.isFooter,
		"!bg-[color:--light-gray-color-v2] !text-[color:--text-alert-color]": hints.isNegative && hints.isFooter,
		"bg-[#b3b3b3] !text-white !font-montserrat": hints.isSubTotalRow,
		"mt-6": hasNoEarnings,
		"mb-6": hints.hasNoDeductions,
		"bg-[color:--light-gray-color-v2]": hints.isSubTotalRow && hints.isNegative,
		"shadow-[inset_0_0_0_1px_#b3b3b3] bg-[#e8e8e8] rounded-[6px]": hints.isPreviousEdited,
		"table-noWrap": hints.noWrap,
	});
};

const getCellType = ({ value, definitions, defaultType }: getCellTypeParams) => {
	const { type, input } = definitions || {};
	if (!isNumeric(value)) {
		return "string";
	}
	if (input) {
		return input;
	}
	return type ?? defaultType ?? "string";
};

const getIndexItem = (data: CompensationDataRecord[] = [], item = "") =>
	data?.findIndex((element) => element.item?.toUpperCase() === item?.toUpperCase());

const SPECIAL_RENDERS = ["CIT BONUS"];
const headerClasses = "bg-[color:--text-color]";
const defaultWidth = 20;

const isSpecialRender = (item: Nullable<string>) => SPECIAL_RENDERS.includes(item?.toUpperCase() ?? "");

const getItemDefinition = (item: Nullable<string>, definitions: CellDefinitions[] = []) =>
	definitions.find((element) => element.item === item);

const getColumnAddons = (field: string, row: { columnsAddons: Array<{ field: string }> }) => {
	if (!field || !row) {
		return null;
	}
	const { columnsAddons } = row || {};
	return (columnsAddons || []).find((element) => element.field === field);
};

const getDecimalPrecision = (value: number, type = "", tag = ""): number => {
	const isUnit = isCellUnit(value, type) && !tag;
	if (isUnit) {
		return 1;
	}
	return 2;
};

const getInputShowValue = ({ value = "", type, tag = "" }: { value: Nullable<string>; type: string; tag?: string }) => {
	if (isEmptyValue(value) || !isNumeric(value)) {
		return value;
	}

	const num = Number(value);
	const isNegative = isNegativeValue(num);
	const isPercentage = isCellValuePercentage(value, type);

	const precision = getDecimalPrecision(num, type, tag);
	let showValue = accountFormat(Math.abs(num), precision);

	if (isNegative) {
		showValue = isPercentage ? `-${showValue}` : `(${showValue})`;
	}
	return showValue;
};

function getProperties(column: GridColDef, index: number, isSomeFlex: boolean): StringMap<number> {
	const isFlex = index === 0 && !isSomeFlex;

	const properties: StringMap<number> = {};
	if (isFlex || column.flex) {
		properties["flex"] = 1;
	} else {
		properties["width"] = column.width ?? defaultWidth;
	}
	return properties;
}

function guessAlignment(column: GridColDef) {
	return column.align ?? "left";
}

function guessType(column: GridColDef) {
	return column.type ?? "string";
}

function getRenderHeaderFn() {
	return (params: GridColumnHeaderParams) => {
		return (
			<p
				className={clsx({
					"text-white font-montserrat text-[14px]": true,
					"w-[600px] text-right": params.colDef.headerAlign === "right",
					"w-[600px] text-left": params.colDef.headerAlign === "left",
				})}
			>
				{params.colDef.headerName}
			</p>
		);
	};
}

function getCellWidth({ colDef }: GridCellParams) {
	if (colDef.flex && colDef.flex > 0) {
		return colDef.computedWidth;
	}
	return colDef.computedWidth ?? colDef.width ?? defaultWidth;
}

function getDefaultRenderFn({
	col,
	column,
	itemsDefinitions,
}: {
	column: GridColDef;
	col: GridColDef & { cellsType?: string };
	itemsDefinitions: CellDefinitions[];
}) {
	return (params: GridRenderCellParams) => {
		if (isEmptyValue(params.row?.item) || isSubTotal(params?.row?.item) || isFooterCell(params)) {
			return <></>;
		}

		const addons = getColumnAddons(params?.field, params?.row);
		const { tag = "" } = (addons ?? {}) as IColumnsAddonsRow;
		const definitions = getItemDefinition(params.row?.item, itemsDefinitions);
		const fieldMatches = and(Boolean(definitions?.columns), Boolean(definitions?.columns?.includes(params.field)));
		const cellDefinitions = or(!definitions?.columns, fieldMatches) ? definitions : undefined;
		const type = getCellType({
			value: params.value,
			definitions: { ...cellDefinitions, ...addons },
			defaultType: col.cellsType ?? column.type,
		});

		const value = params.value;
		const showValue = getInputShowValue({ value, type, tag });

		if (isSpecialRender(params?.row?.item)) {
			return <SpecialRowRender item={params?.row?.item?.toUpperCase()} params={params} />;
		}

		const withTooltip = !isNumeric(params.value) && getCellWidth(params) < measureTextWidth(String(showValue), FONT_SIZE_IN_PX);

		return (
			<TypographyCell
				fontSize={`${FONT_SIZE_IN_PX}px`}
				fontLineHeight={`${FONT_LINE_HEIGHT_IN_PX}px`}
				params={params}
				tooltipProps={withTooltip ? { arrow: true, contentAsTitle: true } : undefined}
				customAfter={tag}
				hints={{
					isUnit: isCellUnit(params.value, type),
					isDollarPerUnit: isCellDollarPerUnit(params.value, type),
					isHours: isCellHours(params.value, type),
					isPercentage: isCellValuePercentage(params.value, type),
					isMoney: isCellValueDollar(params.value, type),
					isNegative: isNegativeValue(params.value),
				}}
			>
				{showValue}
			</TypographyCell>
		);
	};
}

type ExtendedGridColDef = GridColDef & { cellsType?: string };

function guessRenderEditCellFn(props: {
	column: ExtendedGridColDef;
	definitions?: CellDefinitions[];
	properties?: ReturnType<typeof getProperties>;
	inputTypeVariant?: 0 | 1;
	inputAlternative?: string;
	onChangeType?: () => void;
}): GridColDef["renderEditCell"] {
	const { column, inputAlternative = "", inputTypeVariant = 0, onChangeType = () => {} } = props;
	const data = getInputData(column.field, inputAlternative);
	const isBasicTierInput = !data?.length && column.field === "tier";

	if (isBasicTierInput) {
		return (params) => <BasicTextEditCell {...params} inputProps={{ placeholder: column.headerName, acceptsEmptyValues: true }} />;
	}

	if (data?.length) {
		return (params) => (
			<NumericTierEditCell
				{...params}
				data={data}
				inputAlternative={inputAlternative}
				inputTypeVariant={inputTypeVariant}
				onChangeType={onChangeType}
			/>
		);
	}

	return undefined;
}

function guessPreProcessEditCellPropsFn(props: {
	column: ExtendedGridColDef;
	definitions?: CellDefinitions[];
	properties?: ReturnType<typeof getProperties>;
}): GridColDef["preProcessEditCellProps"] {
	const { column } = props;
	if (column.field === "tier" && !column.cellsType) {
		return (params) => {
			return { ...params.props, error: false };
		};
	}

	switch (column.field) {
		case "gross":
		case "tier":
		case "rate":
			return (params) => {
				const valueExists = emptyValidation(params.props.value, { acceptZero: false });
				return { ...params.props, error: !valueExists };
			};
		default:
			break;
	}

	return undefined;
}

const getBuiltColumns = (
	columns: GridColDef[],
	args: { itemsDefinitions: CellDefinitions[]; inputTypeVariant: 0 | 1; onChangeType: () => void },
	rows: GridValidRowModel[],
): GridColDef[] => {
	if (!columns?.length) {
		return [];
	}
	const { itemsDefinitions = [] } = args;
	const isSomeFlex = columns.some((column) => column.flex);
	const inputAlternative = getAlternativeInput(columns);
	return columns.map((column, i): GridColDef => {
		const col = column as GridColDef & { cellsType?: string };
		const properties = getProperties(column, i, isSomeFlex);

		return {
			...column,
			...properties,
			field: column.field,
			headerName: column.headerName,
			align: guessAlignment(column),
			type: guessType(column),
			sortable: false,
			editable: true,
			headerClassName: () => {
				return clsx({
					[headerClasses]: true,
				});
			},
			renderHeader: getRenderHeaderFn(),
			cellClassName: (params) => {
				const addons = getColumnAddons(params?.field, params?.row);
				const definitions = getItemDefinition(params.row?.item, itemsDefinitions);
				const type = getCellType({
					value: params.value,
					definitions: { ...definitions, ...addons },
					defaultType: col.cellsType ?? column.type,
				});
				const isSubTotalRow = isSubTotal(params?.row?.item);
				let hasNoDeductions = false;
				if (isSubTotalRow) {
					const subTotalIndex = rows.findIndex(r => isSubTotal(r.item));
					hasNoDeductions = subTotalIndex !== -1 && !rows.slice(subTotalIndex + 1).length;
				}
				return getCellClasses(
					"",
					{
						isNegative: isNegativeValue(params?.value),
						isSubTotalRow,
						isDollar: isCellValueDollar(params.value, type),
						isPercentage: isCellValuePercentage(params.value, type),
						noWrap: true,
						hasNoDeductions,
					},
					params
				);
			},
			renderCell: getDefaultRenderFn({ column, col, itemsDefinitions }),
			renderEditCell:
				column.renderEditCell ??
				guessRenderEditCellFn({
					inputAlternative,
					column: col,
					inputTypeVariant: args.inputTypeVariant,
					onChangeType: args.onChangeType,
				}),
			preProcessEditCellProps: column.preProcessEditCellProps ?? guessPreProcessEditCellPropsFn({ column: col }),
		};
	});
};

const getVirtualSummary = (summary: CompensationDataRecord[] = [], addedCompensations: CompensationDataRecord[] = []) => {
	addedCompensations.sort((a, b) => {
		if (a.rowIndex === b.rowIndex) {
			return a.order - b.order;
		}
		return a.rowIndex - b.rowIndex;
	});
	let virtual: CompensationDataRecord[] = [];
	let temp: CompensationDataRecord[] = [];
	let lastIndex = 0;
	summary.forEach((item, index) => {
		temp = addedCompensations.filter((addedCompensation) => addedCompensation.rowIndex === index);
		virtual = [...virtual, ...temp, item];
		lastIndex = index;
	});
	temp = addedCompensations.filter((addedCompensation) => addedCompensation.rowIndex > lastIndex);
	return [...virtual, ...temp];
};

function handleAddedCompensationsDeleteAction(addedCompensations: CompensationDataRecord[], oldRow: CompensationDataRecord) {
	let newAddedCompensations = [...addedCompensations];
	const indexToRemove = addedCompensations.findIndex((element) => element._id === oldRow._id && element.order === oldRow.order);
	if (indexToRemove > -1) {
		newAddedCompensations.splice(indexToRemove, 1);
		newAddedCompensations = newAddedCompensations.map((element) => {
			if (element.rowIndex === oldRow.rowIndex && element.order > oldRow.order) {
				element.order--;
			}
			return element;
		});
	}
	return newAddedCompensations;
}

function handleAddedCompensationsAddAction({
	addedCompensations,
	newRow,
	virtualSummary,
	rawSummary,
}: Readonly<{
	addedCompensations: CompensationDataRecord[];
	newRow: CompensationDataRecord;
	virtualSummary: GridValidRowModel[];
	rawSummary: CompensationDataRecord[];
}>) {
	let newAddedCompensations = [...addedCompensations];
	const summary = virtualSummary as CompensationDataRecord[];
	const globalIndex = newRow.rowIndex;
	const rowBefore = globalIndex > 0 ? summary[globalIndex - 1] : null;
	if (!rowBefore) {
		const rowAfter = globalIndex < summary.length - 1 ? summary[globalIndex + 1] : null;
		if (and(Boolean(rowAfter), !isNaN(Number(rowAfter?.rowIndex)))) {
			newRow.rowIndex = rowAfter?.rowIndex;
			newRow.order = rowAfter?.order > 0 ? rowAfter?.order - 1 : 0;
		} else if (rowAfter) {
			const relativeIndex = rawSummary.findIndex((element) => element._id === rowAfter._id);
			newRow.rowIndex = relativeIndex;
			newRow.order = 0;
		}
	} else if (!isNaN(Number(rowBefore?.rowIndex))) {
		newRow.rowIndex = rowBefore.rowIndex;
		newRow.order = rowBefore.order + 1;
	} else {
		const relativeIndex = rawSummary.findIndex((element) => element._id === rowBefore._id);
		newRow.rowIndex = relativeIndex + 1;
		newRow.order = 0;
	}
	newAddedCompensations.push(newRow);
	newAddedCompensations = newAddedCompensations.map((element) => {
		if (and(element.rowIndex === newRow.rowIndex, element.order >= newRow.order, element._id !== newRow._id)) {
			element.order++;
		}
		return element;
	});
	return newAddedCompensations;
}

const getSanitizedSummary = (summary: CompensationDataRecord[] = []) =>
	summary.filter((definition: CompensationDataRecord) => !definition.hasOwnProperty("rowIndex") && definition?.item);

export default function CompensationTable(props: Readonly<CompensationTableProps>) {
	const {
		data = [],
		columns = [],
		itemsDefinitions = [],
		itemsLabelsSubstitutions = { "Customer Satisfaction": "Customer Satisfaction Index (CSI)" },
		title = "Compensation",
		editable = false,
		tooltip = false,
		onActionUpdate = () => {},
		loading = false,
		overrideColumns = {},
	} = props;
	const containerRef = useRef<HTMLDivElement>(null);
	const apiRef = useGridApiRef();
	const user = useSelector((state: { user: { user: IUser } }) => state.user.user);
	const [rows, setRows] = useState<CompensationDataRecord[]>([]);
	const [addedCompensations, setAddedCompensations] = useState<CompensationDataRecord[]>([]);
	const [isLoading, setIsLoading] = useState(loading);
	const [inputTypeVariant, setInputTypeVariant] = useState<0 | 1>(0);

	const updateAddedCompensations = async ({
		action,
		options,
	}: {
		action?: "del" | "add" | "mod";
		options?: { newRow?: CompensationDataRecord; oldRow?: CompensationDataRecord; summary?: GridValidRowModel[] };
	}) => {
		setIsLoading(true);
		let newAddedCompensations = [...addedCompensations];
		if (action === "del" && options?.oldRow) {
			newAddedCompensations = handleAddedCompensationsDeleteAction(addedCompensations, options?.oldRow);
		} else if (action === "add" && options?.newRow && options?.summary) {
			newAddedCompensations = handleAddedCompensationsAddAction({
				addedCompensations,
				newRow: options.newRow,
				virtualSummary: options.summary,
				rawSummary: data,
			});
		} else if (action === "mod" && options?.newRow) {
			const { newRow } = options;
			const indexToUpdate = addedCompensations.findIndex((element) => element._id === newRow._id);
			if (indexToUpdate > -1) {
				newAddedCompensations[indexToUpdate] = newRow;
			}
		}
		setAddedCompensations(newAddedCompensations);
		setRows(getVirtualSummary(data, newAddedCompensations));
		onActionUpdate?.({ addedCompensations: newAddedCompensations });
	};

	const handleCommissionChange = ({
		newRow,
		oldRow,
		summary,
	}: // subTotalIndex,
	{
		newRow: CompensationDataRecord;
		oldRow: CompensationDataRecord;
		summary: GridValidRowModel[];
		subTotalIndex?: number;
	}) => {
		const { commission } = oldRow || {};
		const { item, commission: newCommission } = newRow || {};

		const currentRowIndex = getIndexItem(summary, item);
		const isDifferentValue = isDifferentCommission(newCommission, commission);
		const isMutable = currentRowIndex !== -1 && isDifferentValue;

		if (!isMutable) {
			return;
		}

		summary[currentRowIndex]["modifiedBy"] = user.fullName ?? "";
		summary[currentRowIndex].commission = newCommission;

		setIsLoading(true);
		setRows(summary);
		const input = { summary: { summary: getSanitizedSummary(summary), total: 0 } };
		onActionUpdate?.(input);
	};

	// Handlers
	const handleEditCompensation = async (newRow: GridValidRowModel, oldRow: GridValidRowModel) => {
		const summary = [...apiRef.current.getRowModels()].map((element) => element[1]);
		const subTotalIndex = getIndexItem(summary, "COMMISSION SUBTOTAL");
		if (!newRow) {
			await updateAddedCompensations({ action: "del", options: { oldRow } });
			return newRow;
		}
		const { rowIndex } = newRow || {};
		if (!isNaN(Number(rowIndex))) {
			const addedRowIndex = addedCompensations.findIndex((element) => element._id === newRow._id);
			if (addedRowIndex === -1) {
				newRow.originalCommission = newRow.commission;
				newRow.modifiedBy = user.fullName ?? "";
				newRow.isAfterSubTotal = rowIndex > subTotalIndex;
				await updateAddedCompensations({ action: "add", options: { newRow, summary } });
			} else {
				newRow.modifiedBy = user.fullName ?? "";
				await updateAddedCompensations({ action: "mod", options: { newRow } });
			}
			return newRow;
		} else {
			handleCommissionChange({ newRow, oldRow, summary, subTotalIndex });
		}
		return newRow;
	};

	const builtColumns = getBuiltColumns(columns, {
		itemsDefinitions,
		inputTypeVariant,
		onChangeType: () => setInputTypeVariant(inputTypeVariant === 0 ? 1 : 0),
	}, rows);
	const commissionColumn: GridColDef = {
		editable,
		field: "commission",
		headerName: "Comm. ($)",
		width: 130,
		type: "number",
		align: "right",
		sortable: false,
		headerClassName: headerClasses,
		headerAlign: "left",
		renderHeader: getRenderHeaderFn(),
		cellClassName: (params) => {
			let useValue = params.value;
			if (isSubTotal(params.row?.item)) {
				useValue = params.row.commission;
			}
			const isNegative = isNegativeValue(useValue);
			const isSubTotalRow = isSubTotal(params?.row?.item);
			const isDifferentValue = isDifferentCommission(params.row.commission, params.row.originalCommission);
			const isPreviousEdited = isDifferentValue && !isSubTotalRow;
			return getCellClasses("", { isNegative, isPreviousEdited, isSubTotalRow, isDollar: true, hasNoDeductions: getHasNoDeductions(params) }, params);
		},
		renderCell: (params) => {
			let useValue = params.value;
			const itemDesc = params.row?.item ?? "";
			const isSubTotalRow = isSubTotal(itemDesc);
			if (isSubTotalRow) {
				useValue = params.row.commission;
			}
			return (
				<CompensationTooltip
					tooltip={tooltip}
					isNegative={isNegativeValue(useValue)}
					showValue={accountFormat(Math.abs(useValue))}
					user={user}
					params={params}
					hints={{
						isMoney: true,
						isSubTotalRow,
					}}
				/>
			);
		},
		renderEditCell: (params) => <CommissionEditCell {...params} />,
		preProcessEditCellProps: (params) => {
			const isAddedCompensation = !isNaN(Number(params.row?.rowIndex));
			const valueExists = emptyValidation(params.props.value, { acceptZero: !isAddedCompensation });
			return { ...params.props, error: !valueExists };
		},
		valueGetter: (value, row) => {
			if (isSubTotal(row?.item)) {
				return 0;
			}
			return value || 0;
		},
		...(overrideColumns?.commission ?? {}),
	};
	const itemColumn: GridColDef = {
		editable,
		field: "item",
		headerName: "Item Description",
		minWidth: 180,
		flex: 1,
		sortable: false,
		headerClassName: headerClasses,
		renderHeader: getRenderHeaderFn(),
		cellClassName: (params) => {
			return getCellClasses(
				"",
				{
					isSubTotalRow: isSubTotal(params.row?.item),
					noWrap: true,
					hasNoDeductions: getHasNoDeductions(params),
				},
				params
			);
		},
		renderCell: (params) => {
			let contents = params.formattedValue ?? params.value;
			let hints: Partial<ClassNameHints> = { isSubTotalRow: isSubTotal(params.row?.item) };
			if (isFooterCell(params)) {
				contents = "Total Net Due";
				hints = { noWrap: true, noFocus: true };
			}
			const textWidth = measureTextWidth(contents, FONT_SIZE_IN_PX);
			const shouldShowTooltip = textWidth > (params.colDef.computedWidth ?? 0);
			return (
				<Stack direction="row" alignItems="center">
					<TypographyCell
						fontSize={`${FONT_SIZE_IN_PX}px`}
						fontLineHeight={`${FONT_LINE_HEIGHT_IN_PX}px`}
						params={params}
						hints={hints}
						tooltipProps={shouldShowTooltip ? { arrow: true, contentAsTitle: true } : undefined}
					>
						{contents}
					</TypographyCell>
					{!isNaN(Number(params.row?.rowIndex)) ? (
						<Stack className="relative top-[2px] left-[9px] min-w-[16px] min-h-[16px]">
							<Tooltip title={`Added by ${params.row?.modifiedBy}`} placement="right" arrow>
								<img src={InfoIcon} alt="Information icon" />
							</Tooltip>
						</Stack>
					) : null}
				</Stack>
			);
		},
		renderEditCell: (params) => (
			<BasicTextEditCell {...params} inputProps={{ placeholder: "Item Description", acceptsEmptyValues: false, autoFocus: true }} />
		),
		preProcessEditCellProps: (params) => {
			const val = params.props.value;
			if (!val) {
				return { ...params.props, error: true };
			}
			let isRepeated = false;
			const rowModelEntries = apiRef.current.getRowModels().entries();
			for (const [rowId, rowModel] of rowModelEntries) {
				isRepeated = rowId !== params.row._id && rowModel.item?.toUpperCase() === val?.toUpperCase();
				if (isRepeated) {
					break;
				}
			}
			const isOk = String(val).trim().length > 0 && !isRepeated;
			return { ...params.props, error: !isOk };
		},
		valueFormatter: (value) => {
			if (itemsLabelsSubstitutions?.[value]) {
				return itemsLabelsSubstitutions?.[value];
			}
			return value;
		},
		...(overrideColumns?.item ?? {}),
	};

	const getHasNoDeductions = (params: GridCellParams) => {
		let hasNoDeductions = false;
		if (isSubTotal(params?.row?.item)) {
			const subTotalIndex = rows.findIndex(r => isSubTotal(r.item));
			hasNoDeductions = subTotalIndex !== -1 && !rows.slice(subTotalIndex + 1).length;
		}
		return hasNoDeductions;
	}

	const definedColumns: GridColDef[] = [ID_GRID_COLDEF, COLUMNS_ADDONS_DEF, itemColumn, ...builtColumns, commissionColumn];

	useEffect(() => {
		props.addedData && setAddedCompensations(props.addedData);
		setRows(getVirtualSummary(data, props.addedData));
		setIsLoading(loading);
		return () => {
			setRows([]);
			setAddedCompensations([]);
			setIsLoading(false);
			setInputTypeVariant(0);
		};
	}, [data, props.addedData, props.loading]);

	const LoadingOverlayWrapperFn = useCallback(
		() => <CompensationLoadingOverlay rows={rows} defaultRowHeight={DEFAULT_ROW_HEIGHT} />,
		[rows]
	);

	return (
		<Stack className="relative [&_.MuiDataGrid-row--editing]:!shadow-none" ref={containerRef}>
			<CompensationRowAdder
				apiRef={apiRef}
				containerRef={containerRef}
				editable={editable}
				rowsState={[rows, setRows]}
				changeHandler={handleEditCompensation}
			/>
			<Typography className="!font-montserrat !font-medium !text-[28px] !text-white !bg-[color:--text-color] pl-[10px] relative top-[3px] z-10 rounded-t-[8px]">
				{title}
			</Typography>
			<DataSmartTable
				{...props.tableProps}
				loading={isLoading}
				apiRef={apiRef}
				columns={definedColumns}
				rows={rows}
				hasFlexibleRowHeight
				disableHeaderBottomSeparator
				hideFooter
				disableColumnResize={true}
				disableColumnMenu={true}
				disableRowSelectionOnClick={true}
				disableMultipleRowSelection={true}
				checkboxSelection={false}
				columnVisibilityModel={{
					_id: false,
					columnsAddons: false,
				}}
				initialState={{
					aggregation: {
						model: {
							commission: "sum",
						},
					},
				}}
				isCellEditable={isCellEditable}
				editMode="row"
				slots={{
					noRowsOverlay: NoRowsOverlay,
					loadingOverlay: LoadingOverlayWrapperFn,
				}}
				slotProps={{
					noRowsOverlay: {
						title: "No Compensation Data",
					},
				}}
				getRowHeight={(params) => {
					if (String(params?.model?.item || "").toUpperCase() === "CIT BONUS") {
						return "auto";
					}
					return "auto";
				}}
				customProps={{}}
				processRowUpdate={handleEditCompensation}
				onProcessRowUpdateError={(error) => {
					errorLogger(getError(error));
				}}
				onRowEditStart={(_params, event) => {
					const editRows = apiRef.current?.state.editRows ?? {};
					const editModeActive = (Object.keys(editRows).length ?? 0) > 0;
					if (editModeActive) {
						event.defaultPrevented = true;
						event.defaultMuiPrevented = true;
					}
				}}
			/>
		</Stack>
	);
}
